import { useRef, useState } from 'react';

import {
  COGNITIVE_BEHAVIORAL_VERBS,
  AFFECTIVE_BEHAVIORAL_VERBS,
  PSYCHOMOTOR_BEHAVIORAL_VERBS,
  AFFECTIVE_SECTIONS_CONFIG,
} from 'apps/web/constants/forms/objective/behavioralVerbs';
import OBJECTIVE_FORM_KEYS from 'apps/web/constants/forms/objective/formKeys';
import { AffectiveType } from 'apps/web/enums/iPlanObjectives/categories.enum';
import useAutosizeTextArea from 'apps/web/hooks/useAutoSizeTextArea';
import processInput from 'apps/web/utils/formInput';

import {
  LC_PAGE_COMPETENCY_CHAR_LIMIT,
  LC_PAGE_SUBJECT_CHAR_LIMIT,
} from '@lessonPlanner/constants';

import { Button } from '../Button';
import {
  CategorizedComboboxField,
  TextAreaField,
  TextField,
  ComboboxFieldPair,
} from '../form-fields';
import { handleRemovePair } from '../form-fields/ComboboxFieldPair';
import TranslationDropdown from '../molecules/TranslationDropdown';
import { GradeLevelDropdown } from '../Select';

export default function IplanObjective({
  errors,
  watchedValues,
  triggerValidation,
  setValue,
}: {
  errors;
  watchedValues;
  triggerValidation;
  setValue;
}) {
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const {
    subject,
    gradeLevel,
    competency,
    cognitiveVerbs = [],
    psychomotorVerbs = [],
    language,
  } = watchedValues;

  const createAffectiveGroup = (
    type: AffectiveType,
    watchedValues: unknown,
  ) => {
    const suffixes = ['', '2', '3'];
    const { baseVerbKey, baseItemKey } = AFFECTIVE_SECTIONS_CONFIG[type];

    return {
      verbs: suffixes.map(
        (suffix) => watchedValues[`${baseVerbKey}${suffix}`] || [],
      ),
      lists: suffixes.map(
        (suffix) => watchedValues[`${baseItemKey}${suffix}`] || [],
      ),
    };
  };

  const affectiveValues = createAffectiveGroup(
    AffectiveType.VALUES,
    watchedValues,
  );
  const affectiveAttitudes = createAffectiveGroup(
    AffectiveType.ATTITUDES,
    watchedValues,
  );

  const getInitialSectionCount = (pairs: {
    verbs: string[][];
    lists: string[][];
  }) => {
    return (
      pairs.verbs.reduce((count, verbs, index) => {
        const hasVerbs = verbs?.length > 0;
        const hasLists = pairs.lists[index]?.length > 0;
        return hasVerbs || hasLists ? count + 1 : count;
      }, 0) || 1
    );
  };

  const [affectiveValuesSections, setAffectiveValuesSections] = useState(
    getInitialSectionCount(affectiveValues),
  );

  const [affectiveAttitudesSections, setAffectiveAttitudesSections] = useState(
    getInitialSectionCount(affectiveAttitudes),
  );

  const buttonClassName = `inline-block w-5/6 rounded border py-1 text-sm font-medium 'border-blue-600 bg-white text-blue-600 hover:bg-blue-500 hover:text-white active:bg-blue-600 active:text-white disabled:cursor-not-allowed disabled:border-blue-600 disabled:bg-white disabled:text-blue-600 disabled:opacity-60 disabled:hover:border-blue-600`;

  useAutosizeTextArea(textAreaRef.current, watchedValues.competency);

  const renderAffectiveSection = (
    type: AffectiveType,
    sectionsCount: number,
    setSectionsCount: (count: number) => void,
  ) => {
    const {
      itemOptions,
      pairLabel,
      sectionLabel,
      baseItemKey,
      baseVerbKey,
      buttonText,
    } = AFFECTIVE_SECTIONS_CONFIG[type];

    const handleRemoveSection = (index: number) => {
      handleRemovePair(
        index,
        baseVerbKey,
        baseItemKey,
        sectionsCount,
        setValue,
        watchedValues,
        setSectionsCount,
      );
    };

    return (
      <>
        {Array.from({ length: sectionsCount }).map((unused, index) => (
          <ComboboxFieldPair
            displaySelectedCount={false}
            key={`affective-${type}-section-${index}`}
            errors={errors}
            index={index}
            placeholderWord={'Verb'}
            primaryLabel={sectionLabel}
            primaryOptions={AFFECTIVE_BEHAVIORAL_VERBS}
            secondaryLabel={pairLabel}
            secondaryOptions={itemOptions}
            setValue={setValue}
            totalSections={sectionsCount}
            triggerValidation={triggerValidation}
            watchedValues={watchedValues}
            primaryKey={
              index === 0 ? baseVerbKey : `${baseVerbKey}${index + 1}`
            }
            secondaryKey={
              index === 0 ? baseItemKey : `${baseItemKey}${index + 1}`
            }
            onRemoveSection={handleRemoveSection}
          />
        ))}

        {renderAddSectionButton(
          sectionsCount,
          setSectionsCount,
          buttonText,
          type,
        )}
      </>
    );
  };

  const renderAddSectionButton = (
    sectionsCount: number,
    setSectionsCount: (count: number) => void,
    buttonText: string,
    type: AffectiveType,
  ) => {
    if (sectionsCount >= 3) return null;

    const { baseVerbKey, baseItemKey } = AFFECTIVE_SECTIONS_CONFIG[type];
    const suffix = sectionsCount === 1 ? '' : sectionsCount;

    const isLastSectionValid =
      (watchedValues[`${baseVerbKey}${suffix}`] || []).length > 0 &&
      (watchedValues[`${baseItemKey}${suffix}`] || []).length > 0 &&
      !errors[`${baseVerbKey}${suffix}`] &&
      !errors[`${baseItemKey}${suffix}`];

    return (
      <div className="col-span-full flex w-full justify-center">
        <Button
          className={buttonClassName}
          disabled={!isLastSectionValid}
          type="button"
          onClick={() => setSectionsCount(sectionsCount + 1)}
        >
          + {buttonText}
        </Button>
      </div>
    );
  };

  return (
    <>
      <TextField
        className="col-span-full"
        errorMessage={!!errors.subject && 'Please enter the Subject'}
        id={OBJECTIVE_FORM_KEYS.subject}
        isMaxLengthReached={subject?.length >= LC_PAGE_SUBJECT_CHAR_LIMIT}
        label="Subject"
        maxLength={LC_PAGE_SUBJECT_CHAR_LIMIT}
        name={OBJECTIVE_FORM_KEYS.subject}
        placeholder={'Input the subject for this lesson plan'}
        type="subject"
        value={subject}
        onBlur={() =>
          processInput(
            setValue,
            triggerValidation,
            OBJECTIVE_FORM_KEYS.subject,
            subject?.trim() || '',
          )
        }
        onChange={(e) =>
          processInput(
            setValue,
            triggerValidation,
            OBJECTIVE_FORM_KEYS.subject,
            e.target.value,
          )
        }
      />

      <GradeLevelDropdown
        error={!!errors.gradeLevel}
        errorMessage={!!errors.gradeLevel && 'Please select the Grade Level'}
        value={gradeLevel}
        onBlur={() => {
          triggerValidation(OBJECTIVE_FORM_KEYS.gradeLevel);
        }}
        onChange={(value) =>
          processInput(
            setValue,
            triggerValidation,
            OBJECTIVE_FORM_KEYS.gradeLevel,
            value,
          )
        }
      />

      <TextAreaField
        showSubLabel
        className="col-span-full"
        error={!!errors.competency}
        id={OBJECTIVE_FORM_KEYS.competency}
        isMaxLengthReached={competency?.length >= LC_PAGE_COMPETENCY_CHAR_LIMIT}
        label="Unpacked Learning Competency"
        maxLength={LC_PAGE_COMPETENCY_CHAR_LIMIT}
        name={OBJECTIVE_FORM_KEYS.competency}
        placeholder="Input the learning competency"
        rows={2}
        subLabel="Place one learning competency from the output in Step 1: Learning Competency"
        textAreaRef={textAreaRef}
        type="competency"
        value={competency}
        errorMessage={
          !!errors.competency && 'Please enter the Learning Competency'
        }
        onBlur={() =>
          processInput(
            setValue,
            triggerValidation,
            OBJECTIVE_FORM_KEYS.competency,
            competency?.trim() || '',
          )
        }
        onChange={(e) =>
          processInput(
            setValue,
            triggerValidation,
            OBJECTIVE_FORM_KEYS.competency,
            e.target.value,
          )
        }
      />

      <CategorizedComboboxField
        multiSelect
        twoColumnLayout
        className="col-span-full"
        id={OBJECTIVE_FORM_KEYS.cognitiveVerbs}
        label="Cognitive"
        maxSelections={3}
        options={COGNITIVE_BEHAVIORAL_VERBS}
        placeholder="Select or search for Cognitive Behavioral Verbs"
        subLabel="Select a maximum of 3 verbs"
        value={cognitiveVerbs}
        onChange={(value) =>
          setValue(OBJECTIVE_FORM_KEYS.cognitiveVerbs, value)
        }
      />

      <CategorizedComboboxField
        multiSelect
        twoColumnLayout
        className="col-span-full"
        id={OBJECTIVE_FORM_KEYS.psychomotorVerbs}
        label="Psychomotor"
        maxSelections={3}
        options={PSYCHOMOTOR_BEHAVIORAL_VERBS}
        placeholder="Select or search for Psychomotor Behavioral Verbs"
        subLabel="Select a maximum of 3 verbs"
        value={psychomotorVerbs}
        onChange={(value) =>
          setValue(OBJECTIVE_FORM_KEYS.psychomotorVerbs, value)
        }
      />
      {renderAffectiveSection(
        AffectiveType.ATTITUDES,
        affectiveAttitudesSections,
        setAffectiveAttitudesSections,
      )}
      {renderAffectiveSection(
        AffectiveType.VALUES,
        affectiveValuesSections,
        setAffectiveValuesSections,
      )}

      <TranslationDropdown
        errors={errors}
        handleTriggerValidation={(fieldName) => triggerValidation(fieldName)}
        language={language}
        handleUpdateFieldValue={(fieldName, value) =>
          setValue(fieldName, value)
        }
      />
    </>
  );
}
